import React from 'react';

export interface DecodedCharge {
  fullName: string;
  meaning: string;
  example: string;
  tips: string;
}

export enum BankCategory {
  PUBLIC = 'Public Sector Banks',
  PRIVATE = 'Private Sector Banks',
  SMALL_FINANCE = 'Small Finance Banks',
  PAYMENTS = 'Payments Banks',
  FOREIGN = 'Foreign Banks',
  RRB = 'Regional Rural Banks',
  PAYMENT_SERVICE = 'Payment Services',
}

export enum ChargeCategory {
  DOMESTIC = 'Domestic Bank Charges',
  INTERNATIONAL = 'International Charges',
  UPI = 'UPI Charges',
  GATEWAY = 'Payment Gateway Fees',
}

export interface ApplicableSource {
  sourceId: string; // e.g., 'hdfc', 'sbi', 'gpay'
  amount?: string;
  description?: string;
}

export interface Charge {
  id: string; // slug
  code: string; // e.g., NF
  name: string; // e.g., NFS CHG
  fullName: string; // e.g., Non-Financial Service Charge
  category: ChargeCategory;
  typicalAmount: string;
  country: string; // e.g., 'IN', 'US'
  transactionLimit?: string;
  applicableSources: ApplicableSource[];
  
  // For detail page
  whatItMeans: string;
  whyCharged: string;
  howToAvoid: string[]; // List of tips
}

// Data for Index pages (the cards)
export interface BankIndex {
  id: string;
  slug: string;
  name: string;
  category: BankCategory;
  country: string;
}

export interface UpiAppIndex {
  id: string;
  slug: string;
  name: string;
  country: string;
}

export interface CreditCardIndex {
  id: string;
  slug: string;
  name: string;
  country: string;
}

// Data for Detail pages
export interface BankDetail extends BankIndex {
  charges: any[]; // Kept for type-safety, but will be deprecated
}

export interface UpiAppDetail extends UpiAppIndex {
  charges: any[];
  foundingYear?: string;
  primaryFeatures?: string[];
}

export interface CreditCardDetail extends CreditCardIndex {
  charges: any[];
}


// A unified type for any applicable entity
// Fix: Made `chargeDetails` required as `resolveApplicableEntities` always includes it.
// This resolves the type predicate error in `services/dataService.ts`.
// FIX: Changed type to be a union of intersections to resolve a TypeScript inference issue with type predicates.
// FIX: Making properties within chargeDetails required to resolve type predicate error.
export type ApplicableEntity =
  | (BankIndex & { chargeDetails: { amount: string; description: string } })
  | (UpiAppIndex & { chargeDetails: { amount: string; description: string } })
  | (CreditCardIndex & { chargeDetails: { amount: string; description: string } });


// For comparison tool
export interface ComparisonItem {
  id: string;
  type: string;
  amount: string;
  description: string;
  sourceName: string; // e.g., "HDFC Bank" or "Google Pay"
}

// For AI verification of new charges
export interface ChargeVerificationResult {
  isValid: boolean;
  feedback: string;
  refinedData: {
    fullName: string;
    whatItMeans: string;
    whyCharged: string;
    howToAvoid: string[];
  };
}

// For new unified search suggestions
export interface SearchSuggestion {
  name: string;
  // FIX: Added 'Charge' to the union type to allow search suggestions for specific charges.
  type: 'Bank' | 'UPI' | 'Card' | 'Payment App' | 'Charge';
  path: string;
  details?: string; // e.g., Full name for a charge code
  code?: string; // e.g., 'NF', 'IM'
}

// For AI search with grounding
export interface GroundingSource {
  web?: {
    uri: string;
    title: string;
  };
}

export interface AiSearchResult {
  text: string;
  sources: GroundingSource[];
}